/**
 * Size indicators as glyph OUTLINES.
 *
 * CorelDRAW imports a `<g clip-path>` as a PowerClip, but `<text>` children
 * are not clipped with the curves — they come in as loose grouped text next
 * to the PowerClip. Rendering the label through the same OpenType path
 * pipeline the names/numbers use turns it into ordinary curves that ride
 * inside the panel's PowerClip like everything else.
 */

/** The subset of an opentype.js Font we rely on. */
export interface GlyphFont {
  getAdvanceWidth(text: string, fontSize: number): number;
  getPath(text: string, x: number, y: number, fontSize: number): { toPathData(decimals?: number): string };
}

const round = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Centered label at (x, baseline y): white outline under black fill, same
 * look as sizeLabelMarkup but as paths. Uppercase so no descender crosses the
 * panel edge the baseline sits on.
 */
export function sizeLabelPathMarkup(
  font: GlyphFont,
  x: number,
  y: number,
  text: string,
  fontSize: number,
  strokeWidth: number
): string | null {
  const label = text.toUpperCase();
  if (!label.trim()) return null;
  try {
    const width = font.getAdvanceWidth(label, fontSize);
    const d = font.getPath(label, x - width / 2, y, fontSize).toPathData(3);
    if (!d) return null;
    return (
      `<path d="${d}" fill="none" stroke="#ffffff" stroke-width="${round(strokeWidth)}"` +
      ` stroke-linejoin="round"/>` +
      `<path d="${d}" fill="#000000"/>`
    );
  } catch {
    return null;
  }
}

/** First resolved font in the engine's font cache (fonts load during compose). */
export async function firstLoadedFont(
  fontCache: Map<string, Promise<unknown>>
): Promise<GlyphFont | null> {
  for (const pending of fontCache.values()) {
    const font = (await pending.catch(() => null)) as GlyphFont | null;
    if (font && typeof font.getPath === 'function') return font;
  }
  return null;
}
